import { Component, JSX } from 'react';

import { lazyInject } from '../../context/module';
import { GAService } from '../../services/analytics.service';
import { MetricsService } from '../../services/metrics.service';
import { MetricEventName } from '../../types/metric-events';
import { GAContants } from '../../types/ga.types';
import { RouteProps } from '../../types/route-types';
import styles from './growth-pages.module.scss';

// The tool pages we suggest when the visitor didn't arrive with one. Job-shaped
// pages, not the homepage — a deep link serves their reader better and us too.
const SUGGESTED_TOOLS = [
    'compress-png',
    'merge-image-to-pdf',
    'audio-compressor',
    'compress-mp3',
    'compress-mp4',
    'video-compressor',
    'convert-pdf-to-jpg',
];

interface LinkUsState {
    tool: string;
    copied: string | null;
}

export default class LinkUsComponent extends Component<RouteProps, LinkUsState> {
    @lazyInject(GAService) private gaService: GAService;
    @lazyInject(MetricsService) private metricsService: MetricsService;

    readonly state: LinkUsState = { tool: '', copied: null };

    componentDidMount() {
        // Prefill from ?tool=… so the downloads-page entry points land here
        // with the visitor's actual tool already selected.
        try {
            const tool = new URLSearchParams(window.location.search).get('tool');
            if (tool && /^[a-z0-9-]+$/i.test(tool)) this.setState({ tool });
        } catch { }
    }

    toolName(): string {
        const t = this.state.tool.trim();
        if (!t) return 'XConvert';
        return t.split('-').map(w => w.length > 3 ? w[0].toUpperCase() + w.slice(1) : w.toUpperCase())
            .join(' ').replace(/^Convert /i, '').replace(/ To /g, ' to ');
    }

    url(): string {
        const t = this.state.tool.trim();
        return t ? `https://www.xconvert.com/${t}` : 'https://www.xconvert.com';
    }

    copy(kind: string, text: string) {
        navigator.clipboard.writeText(text).then(() => {
            try {
                this.metricsService.emit(MetricEventName.LINKUS_SNIPPET_COPIED, { kind, tool: this.state.tool || 'home' });
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory.BUTTON,
                    GAContants.EventAction['SHARE-CLICK'],
                    GAContants.EventLabel.NAVIGATE,
                    `linkus-${kind}:${this.state.tool || 'home'}`,
                );
            } catch { }
            this.setState({ copied: kind });
            setTimeout(() => this.setState({ copied: null }), 2000);
        });
    }

    snippetHtml(): string {
        const name = this.toolName();
        return `<a href="${this.url()}">${name === 'XConvert' ? 'XConvert — free online file converter' : `Free ${name} — XConvert`}</a>`;
    }

    snippetMarkdown(): string {
        const name = this.toolName();
        return `[${name === 'XConvert' ? 'XConvert — free online file converter' : `Free ${name} — XConvert`}](${this.url()})`;
    }

    snippetBadge(): string {
        return `<a href="${this.url()}"><img src="https://www.xconvert.com/img/xconvert/xconvert-badge.png" width="240" alt="XConvert — free online file converter" /></a>`;
    }

    snippet(kind: string, title: string, why: string, text: string, preview?: JSX.Element) {
        return <div className={styles.snippetBlock}>
            <h3>{title}</h3>
            <p>{why}</p>
            {preview}
            <div className={styles.snippetRow}>
                <code>{text}</code>
                <button className={styles.copyBtn} onClick={() => this.copy(kind, text)}>
                    {this.state.copied === kind ? 'Copied!' : 'Copy'}
                </button>
            </div>
        </div>;
    }

    render() {
        return <div className={styles.page}>
            <div className={styles.hero}>
                <h1>Add XConvert to Your Site</h1>
                <p>
                    XConvert is free, with no account and no watermark. A link from a blog,
                    class page, wiki or README is the single most effective way to help us —
                    it keeps sending people for years. Pick the tool you actually used, copy
                    a snippet, paste it. Done.
                </p>
            </div>

            <div className={styles.toolRow}>
                <label htmlFor="linkus-tool">Link to:</label>
                <input id="linkus-tool" className={styles.toolInput}
                    placeholder="tool page, e.g. compress-png (empty = homepage)"
                    value={this.state.tool}
                    onChange={e => this.setState({ tool: e.target.value.replace(/[^a-z0-9-]/gi, '') })} />
            </div>
            <div className={styles.chipRow}>
                {SUGGESTED_TOOLS.map(t => (
                    <span key={t} className={styles.chip} onClick={() => this.setState({ tool: t })}>{t}</span>
                ))}
            </div>

            {this.snippet('html', 'Text link (HTML)',
                'For a blog post, docs page or website. An in-context sentence around it beats a bare link.',
                this.snippetHtml())}

            {this.snippet('markdown', 'Text link (Markdown)',
                'For a README, wiki or forum that takes Markdown.',
                this.snippetMarkdown())}

            {this.snippet('badge', 'Banner (HTML)',
                'If you prefer a visual link. The image is hosted by us — no need to download anything.',
                this.snippetBadge(),
                <img className={styles.badgePreview} src="/img/xconvert/xconvert-badge.png" alt="XConvert banner preview" />)}

            <div className={styles.honest}>
                One honest sentence in your own words — &ldquo;I compressed the screenshots with
                this free PNG compressor&rdquo; — helps your readers and us more than any banner.
                Please link only where the tool genuinely fits; that&apos;s what keeps a link worth having.
            </div>

            <div className={styles.footNote}>
                Linked to us? We&apos;d love to know — email <a href="mailto:support@xconvert.com">support@xconvert.com</a>.
                Screenshots and reviews (favourable or not) never need permission.
                More ways to help are on <a href="/how-to-help-xconvert-to-grow">Help XConvert Grow</a>.
            </div>
        </div>;
    }
}
